import { useEffect, useState } from "react";
import axios from "axios";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PRODUCTS, formatCad } from "@/data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/keeper/sales`;
const RANGES = [[4, "4 weeks"], [12, "12 weeks"], [26, "6 months"], [52, "1 year"]];

const Stat = ({ label, value, sub, testid }) => (
  <div className="border border-[#303B2D]/70 rounded-sm px-5 py-4">
    <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/55">{label}</p>
    <p className="mt-1 font-display text-3xl text-[#E8DDC7]" data-testid={testid}>{value}</p>
    {sub && <p className="mt-1 text-[12px] text-[#D3CEC4]/55">{sub}</p>}
  </div>
);

const change = (now, before) => {
  if (!before) return now ? "New this period" : "No sales yet";
  const pct = Math.round(((now - before) / before) * 100);
  return `${pct >= 0 ? "+" : ""}${pct}% vs the period before`;
};

export const KeeperSales = ({ token }) => {
  const [weeks, setWeeks] = useState(12);
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    axios.get(`${API}?weeks=${weeks}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setD(r.data)).catch(() => setD({}));
  }, [token, weeks]);

  const series = (Array.isArray(d?.series) ? d.series : []).map((w) => ({ ...w, label: new Date(`${w.week}T12:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric" }), dollars: w.revenue_cents / 100 }));
  const best = Array.isArray(d?.best_sellers) ? d.best_sellers : [];
  const top = best[0]?.jars || 1;

  return (
    <div className="mt-6" data-testid="keeper-sales">
      <div className="flex flex-wrap gap-2">
        {RANGES.map(([w, label]) => (
          <button key={w} onClick={() => setWeeks(w)} data-testid={`sales-range-${w}`}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] rounded-sm border transition-colors ${weeks === w ? "border-[#B5A642] text-[#B5A642]" : "border-[#303B2D] text-[#D3CEC4]/60 hover:text-[#E8DDC7]"}`}>
            {label}
          </button>
        ))}
      </div>
      {!d ? <p className="mt-8 text-[#D3CEC4]/50">Counting the till…</p> : (
        <>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Revenue" value={formatCad(d.revenue_cents ?? 0)} sub={change(d.revenue_cents ?? 0, d.previous_revenue_cents ?? 0)} testid="sales-revenue" />
            <Stat label="Orders" value={d.orders ?? 0} sub={`${d.standing_share ?? 0}% Standing Orders`} testid="sales-orders" />
            <Stat label="Average order" value={formatCad(d.average_order_cents ?? 0)} testid="sales-aov" />
            <Stat label="Repeat customers" value={`${d.repeat_rate ?? 0}%`} sub={`${d.repeat_customers ?? 0} of ${d.customers ?? 0} customers, all time`} testid="sales-repeat" />
          </div>
          <div className="mt-8 border border-[#303B2D]/70 rounded-sm p-5" data-testid="sales-chart">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/55">Revenue by week</p>
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fill: "#A8A396", fontSize: 11 }} axisLine={{ stroke: "#303B2D" }} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: "#A8A396", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip cursor={{ fill: "rgba(181,166,66,0.08)" }} contentStyle={{ background: "#1C1F1E", border: "1px solid #303B2D", color: "#E8DDC7" }}
                    formatter={(v, _n, p) => [`${formatCad(v * 100)} · ${p.payload.orders} orders`, "Week"]} />
                  <Bar dataKey="dollars" fill="#B5A642" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="mt-8 border border-[#303B2D]/70 rounded-sm p-5" data-testid="sales-best-sellers">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/55">Best sellers · jars sold</p>
            {best.length === 0 ? <p className="mt-4 font-display italic text-[#D3CEC4]/55">No jars sold in this period.</p> : (
              <ul className="mt-4 space-y-3">
                {best.map((b) => (
                  <li key={b.product_id} data-testid={`sales-best-${b.product_id}`}>
                    <div className="flex justify-between text-[14px] text-[#D3CEC4]/85"><span>{PRODUCTS.find((p) => p.id === b.product_id)?.name || b.product_id}</span><span className="font-ledger">{b.jars}</span></div>
                    <div className="mt-1 h-1.5 bg-[#303B2D]/60 rounded-full"><div className="h-full bg-[#B5A642]/80 rounded-full" style={{ width: `${(b.jars / top) * 100}%` }} /></div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {(d.refunded_cents ?? 0) > 0 && <p className="mt-4 text-[12px] text-[#D3CEC4]/55">{formatCad(d.refunded_cents)} refunded in this period, not counted in revenue.</p>}
        </>
      )}
    </div>
  );
};
